import { useEffect, useCallback, useState } from "react";
import { VariantProvider } from "../../contexts/useCurrentVariant";
import { ProductProvider } from "../../contexts/useCurrentProduct";
import { ConfigProvider } from "../../contexts/useConfig";
import { ViewerModeProvider } from "../../contexts/useViewerMode";
import { ActionProvider } from "../../contexts/useAction";
import { LocalSessionSettingsProvider } from "../../contexts/useLocalSessionSettings";
import { HotspotEditorProvider, useHotspotEditor } from "../../contexts/useHotspotEditor";
import { EditorConfigProvider } from "../../contexts/useEditorConfig";
import { VideoModalProvider } from "../../contexts/useVideoModal";
import { HotspotModalProvider } from "../../contexts/useHotspotModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";

import HotspotEditor from "./HotspotEditor";
import EditorModelViewer from "./EditorModelViewer";
import GeneralEditor from "./GeneralEditor";
import GeneralEditorViewer from "./GeneralEditorViewer";

import "./EditorApp.scss";

// Inner component to handle keyboard shortcuts for hotspot mode
function HotspotKeyboardHandler({ children }) {
   const { editorMode, setEditorMode, selectedHotspotIndex, deleteHotspot } = useHotspotEditor();

   const handleKeyDown = useCallback((e) => {
      // Escape to cancel current mode
      if (e.key === 'Escape') {
         if (editorMode !== 'select') {
            setEditorMode('select');
         }
      }

      // Delete or Backspace to delete selected hotspot (when not typing in input)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedHotspotIndex !== null) {
         const activeElement = document.activeElement;
         const isInputFocused = activeElement.tagName === 'INPUT' || 
                                activeElement.tagName === 'TEXTAREA' ||
                                activeElement.tagName === 'SELECT';
         
         if (!isInputFocused) {
            e.preventDefault();
            deleteHotspot(selectedHotspotIndex);
         }
      }

      // Keyboard shortcuts for modes
      if (e.key === 'a' && !e.ctrlKey && !e.metaKey) {
         const activeElement = document.activeElement;
         const isInputFocused = activeElement.tagName === 'INPUT' || 
                                activeElement.tagName === 'TEXTAREA' ||
                                activeElement.tagName === 'SELECT';
         if (!isInputFocused) {
            setEditorMode('add');
         }
      }
   }, [editorMode, setEditorMode, selectedHotspotIndex, deleteHotspot]);

   useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
   }, [handleKeyDown]);

   return children;
}

function EditorTabs({ activeTab, setActiveTab }) {
   return (
      <div className="editor-tabs">
         <button 
            className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
         >
            <FontAwesomeIcon icon={faCog} />
            <span>General</span>
         </button>
         <button 
            className={`tab-btn ${activeTab === 'hotspots' ? 'active' : ''}`}
            onClick={() => setActiveTab('hotspots')}
         >
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            <span>Hotspots</span>
         </button>
      </div>
   );
}

function EditorContent() {
   const [activeTab, setActiveTab] = useState('general');

   return (
      <div className="editor-app">
         <EditorTabs activeTab={activeTab} setActiveTab={setActiveTab} />
         
         {activeTab === 'general' && (
            <div className="editor-panel general-panel">
               <GeneralEditorViewer />
               <GeneralEditor />
            </div>
         )}
         
         {activeTab === 'hotspots' && (
            <HotspotKeyboardHandler>
               <div className="editor-panel hotspot-panel">
                  <EditorModelViewer />
                  <HotspotEditor />
               </div>
            </HotspotKeyboardHandler>
         )}
      </div>
   );
}

export default function EditorApp() {
   return (
      <ProductProvider>
         <VariantProvider>
            <LocalSessionSettingsProvider>
               <ViewerModeProvider>
                  <ActionProvider>
                     <ConfigProvider>
                        <EditorConfigProvider>
                           <HotspotModalProvider>
                              <VideoModalProvider>
                                 <HotspotEditorProvider>
                                    <EditorContent />
                                 </HotspotEditorProvider>
                              </VideoModalProvider>
                           </HotspotModalProvider>
                        </EditorConfigProvider>
                     </ConfigProvider>
                  </ActionProvider>
               </ViewerModeProvider>
            </LocalSessionSettingsProvider>
         </VariantProvider>
      </ProductProvider>
   );
}


